import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowRight, Check, CreditCard, Landmark, PawPrint, Search, Smartphone } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import MaskedLines from "../components/MaskedLines";
import api from "../lib/api";

const whenText = (b) => {
  if (!b.start_date) return "—";
  const start = format(new Date(`${b.start_date}T00:00:00`), "EEE, MMM d");
  if (b.end_date) {
    const end = format(new Date(`${b.end_date}T00:00:00`), "MMM d");
    return `${start} – ${end}`;
  }
  const walk = b.service_slug === "dog-walking" && b.walk_length ? ` · ${b.walk_length}` : "";
  return `${start} · ${b.time_slot}${walk}`;
};

const Pay = () => {
  const [searchParams] = useSearchParams();
  const [refInput, setRefInput] = useState(searchParams.get("ref") || "");
  const [booking, setBooking] = useState(null);
  const [settings, setSettings] = useState({ venmo_handle: "", zelle_info: "" });
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);

  const lookup = async (ref, quiet = false) => {
    const clean = (ref || "").trim().toUpperCase();
    if (!/^PL-[A-Z0-9]{6}$/.test(clean)) {
      if (!quiet) toast.error("References look like PL-XXXXXX — check your confirmation email or screen.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/payments/booking/${clean}`);
      setBooking(data);
      setRefInput(clean);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (!quiet) toast.error(typeof detail === "string" ? detail : "Couldn't find that booking.");
      setBooking(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/payment-settings").then((r) => setSettings(r.data)).catch(() => {});
    const pre = searchParams.get("ref");
    const sid = searchParams.get("session_id");
    if (pre) lookup(pre, true);
    if (sid) {
      setPolling(true);
      let tries = 0;
      pollRef.current = setInterval(async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === "paid") {
            clearInterval(pollRef.current);
            setPolling(false);
            if (pre) lookup(pre, true);
            toast.success("Payment received — thank you!");
          } else if (tries > 8) {
            clearInterval(pollRef.current);
            setPolling(false);
          }
        } catch {
          if (tries > 8) setPolling(false);
        }
      }, 2000);
    }
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const payCard = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        reference: booking.reference,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't start the payment — please try again.");
      setPaying(false);
    }
  };

  const manual = async (method) => {
    try {
      await api.post("/payments/manual", { reference: booking.reference, method });
      setBooking({ ...booking, payment_status: "awaiting_confirmation", payment_method: method });
      toast.success("Noted! Ari will confirm as soon as it lands.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't record that — please try again.");
    }
  };

  const paid = booking?.payment_status === "paid";
  const awaiting = booking?.payment_status === "awaiting_confirmation";
  const approved = booking?.status === "approved";

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="pt-28 md:pt-36">
        <div className="mx-auto max-w-[760px] px-6 pb-28 md:px-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-marmalade">Pay for your sit</p>
          <h1 className="mt-4 font-display text-5xl font-medium leading-[0.98] tracking-tight text-ink md:text-6xl">
            <MaskedLines lines={[<span key="a">Settle the</span>, <span key="b"><em className="text-marmalade">tail</em> bill.</span>]} />
          </h1>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-inkSoft md:text-lg">
            Enter your booking reference (it starts with PL-) to see your total and pay by card, Apple Pay,
            Venmo or Zelle.
          </p>

          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <input
              value={refInput}
              onChange={(e) => setRefInput(e.target.value.toUpperCase())}
              placeholder="PL-XXXXXX"
              data-testid="pay-reference-input"
              className="w-full flex-1 rounded-2xl border border-line bg-white px-5 py-4 font-mono text-base tracking-[0.2em] text-ink placeholder:text-inkSoft/50 transition-[border-color] duration-200 focus:border-marmalade focus:outline-none"
            />
            <button
              onClick={() => lookup(refInput)}
              disabled={loading}
              data-testid="pay-lookup-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-forest disabled:opacity-60"
            >
              {loading ? "Looking…" : "Find booking"}
              <Search className="h-4 w-4" />
            </button>
          </div>

          {booking && (
            <div className="mt-10 rounded-3xl border border-line bg-white p-8 md:p-10" data-testid="pay-booking-card">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-inkSoft">{booking.reference}</p>
                  <h2 className="mt-2 font-display text-3xl font-medium text-ink">{booking.service_name}</h2>
                  <p className="mt-1 text-sm text-inkSoft">
                    {booking.pet_name} · {whenText(booking)}
                  </p>
                </div>
                <div className="text-right">
                  <span className="font-display text-4xl font-semibold text-ink">${Number(booking.price_total).toFixed(2)}</span>
                  <span className="block text-xs text-inkSoft">{booking.price_note}</span>
                </div>
              </div>

              {paid ? (
                <div className="mt-8 flex items-center gap-3 rounded-2xl bg-forest/10 p-5 text-forest" data-testid="pay-paid-banner">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-forest text-cream">
                    <Check className="h-5 w-5" />
                  </span>
                  <p className="text-sm font-medium">
                    Paid{booking.payment_method === "venmo" || booking.payment_method === "zelle" ? ` by ${booking.payment_method}` : " by card"} — thank you!
                  </p>
                </div>
              ) : awaiting ? (
                <div className="mt-8 rounded-2xl border border-line bg-sand p-5 text-sm leading-relaxed text-ink" data-testid="pay-awaiting-banner">
                  Marked as sent by {booking.payment_method}. Ari will confirm the moment it lands — no need to
                  send it twice.
                </div>
              ) : !approved ? (
                <div className="mt-8 rounded-2xl border border-line bg-sand p-5 text-sm leading-relaxed text-inkSoft" data-testid="pay-not-approved-banner">
                  Your request is in — payment opens the moment Ari approves it.
                </div>
              ) : (
                <>
                  {polling && (
                    <p className="mt-8 rounded-2xl bg-sand p-4 text-sm text-inkSoft">Confirming your payment…</p>
                  )}
                  <div className="mt-8 space-y-4">
                    <button
                      onClick={payCard}
                      disabled={paying}
                      data-testid="pay-card-btn"
                      className="group flex w-full items-center justify-between rounded-2xl border border-line bg-cream p-5 text-left transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-marmalade disabled:opacity-60"
                    >
                      <span className="flex items-center gap-4">
                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-marmalade/15">
                          <CreditCard className="h-5 w-5 text-marmalade" />
                        </span>
                        <span>
                          <span className="block font-semibold text-ink">Card · Apple Pay · Google Pay</span>
                          <span className="block text-xs text-inkSoft">Secure checkout by Stripe</span>
                        </span>
                      </span>
                      <ArrowRight className="h-5 w-5 text-marmalade transition-transform duration-300 group-hover:translate-x-1" />
                    </button>

                    {(settings.venmo_handle || settings.zelle_info) && (
                      <div className="rounded-2xl border border-line bg-cream p-5" data-testid="pay-manual-panel">
                        <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                          <Smartphone className="h-4 w-4 text-forest" />
                          Or pay straight from your banking app
                        </p>
                        {settings.venmo_handle && (
                          <p className="mt-3 text-sm text-ink">
                            Venmo: <span className="font-mono font-semibold">{settings.venmo_handle}</span> — send ${Number(booking.price_total).toFixed(2)} and put "{booking.reference}" in the note.
                          </p>
                        )}
                        {settings.zelle_info && (
                          <p className="mt-2 text-sm text-ink">
                            Zelle: <span className="font-mono font-semibold">{settings.zelle_info}</span> — same amount, same note.
                          </p>
                        )}
                        <div className="mt-4 flex flex-wrap gap-3">
                          {settings.venmo_handle && (
                            <button
                              onClick={() => manual("venmo")}
                              data-testid="pay-venmo-sent-btn"
                              className="rounded-full bg-forest px-6 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-ink"
                            >
                              I sent it on Venmo
                            </button>
                          )}
                          {settings.zelle_info && (
                            <button
                              onClick={() => manual("zelle")}
                              data-testid="pay-zelle-sent-btn"
                              className="rounded-full bg-forest px-6 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-ink"
                            >
                              I sent it on Zelle
                            </button>
                          )}
                        </div>
                        <p className="mt-3 flex items-center gap-2 text-xs text-inkSoft">
                          <Landmark className="h-3.5 w-3.5" />
                          Ari confirms manually — usually within a couple of hours.
                        </p>
                      </div>
                    )}
                    <p className="flex items-center gap-2 text-xs text-inkSoft">
                      <PawPrint className="h-3.5 w-3.5 text-marmalade" />
                      Questions about your total? <Link to="/messages" className="underline">Message Ari</Link>.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Pay;
